import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useParticipants, useLocalParticipant } from '@livekit/components-react';
import { Track, ParticipantEvent, TrackEvent } from 'livekit-client';
import { Users, MicOff, Mic, Camera, VideoOff } from 'lucide-react';
import useVideoStore from '../../stores/videoStore';
import { useTranslation } from '../../i18n';

const parseRole = (metadata) => {
  if (!metadata) return null;
  try {
    return JSON.parse(metadata).role || null;
  } catch {
    return null;
  }
};

/**
 * Single participant video tile with real video
 */
const ParticipantTile = ({ participant, isLocal, showTeacherControls }) => {
  const { t } = useTranslation();
  const attachedElRef = useRef(null);
  const [videoTrack, setVideoTrack] = useState(null);
  const [isCameraMuted, setIsCameraMuted] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(!participant.isMicrophoneEnabled);

  // Track which publication exists + its mute state. We must re-render on
  // mute/unmute so the <video> element can mount/unmount correctly.
  useEffect(() => {
    const updateTrack = () => {
      const publications = Array.from(participant.trackPublications.values());
      const cameraPublication = publications.find(pub => pub.source === Track.Source.Camera);
      setVideoTrack(cameraPublication?.track || null);
      setIsCameraMuted(!!cameraPublication?.isMuted);
      setIsMicMuted(!participant.isMicrophoneEnabled);
    };

    updateTrack();

    participant.on(ParticipantEvent.TrackSubscribed, updateTrack);
    participant.on(ParticipantEvent.TrackUnsubscribed, updateTrack);
    participant.on(ParticipantEvent.TrackPublished, updateTrack);
    participant.on(ParticipantEvent.TrackUnpublished, updateTrack);
    participant.on(ParticipantEvent.LocalTrackPublished, updateTrack);
    participant.on(ParticipantEvent.LocalTrackUnpublished, updateTrack);
    participant.on(ParticipantEvent.TrackMuted, updateTrack);
    participant.on(ParticipantEvent.TrackUnmuted, updateTrack);

    return () => {
      participant.off(ParticipantEvent.TrackSubscribed, updateTrack);
      participant.off(ParticipantEvent.TrackUnsubscribed, updateTrack);
      participant.off(ParticipantEvent.TrackPublished, updateTrack);
      participant.off(ParticipantEvent.TrackUnpublished, updateTrack);
      participant.off(ParticipantEvent.LocalTrackPublished, updateTrack);
      participant.off(ParticipantEvent.LocalTrackUnpublished, updateTrack);
      participant.off(ParticipantEvent.TrackMuted, updateTrack);
      participant.off(ParticipantEvent.TrackUnmuted, updateTrack);
    };
  }, [participant, isLocal]);

  const hasVideo = !!videoTrack && !isCameraMuted;

  // Callback ref handles attach/detach on every mount/unmount of the <video>
  // element. A useEffect keyed on `videoTrack` alone misses the case where
  // the element is conditionally unmounted (hasVideo flip) without the track
  // reference changing — which left the remounted element unattached and
  // showed a blank white tile after toggling camera off → on.
  const setVideoEl = useCallback((node) => {
    const prevEl = attachedElRef.current;
    if (prevEl && videoTrack && prevEl !== node) {
      videoTrack.detach(prevEl);
    }
    attachedElRef.current = node;
    if (node && videoTrack) {
      videoTrack.attach(node);
    }
  }, [videoTrack]);

  // Re-attach when the underlying mediaStreamTrack is swapped (e.g. the
  // upstream SDK restart path) — keeps the same <video> element wired up.
  useEffect(() => {
    if (!videoTrack || !attachedElRef.current) return;
    const reattach = () => {
      if (attachedElRef.current && videoTrack) {
        videoTrack.attach(attachedElRef.current);
      }
    };
    videoTrack.on(TrackEvent.Restarted, reattach);
    return () => {
      videoTrack.off(TrackEvent.Restarted, reattach);
    };
  }, [videoTrack]);

  // Parse participant name
  const displayName = isLocal
    ? t('video.you')
    : participant.name || participant.identity || t('video.participant');

  // Check if teacher based on metadata or name
  const isTeacher = useMemo(() => {
    const role = parseRole(participant.metadata);
    if (role) return role === 'teacher';
    return participant.name?.toLowerCase().includes('вчитель') ||
           participant.identity?.includes('teacher');
  }, [participant.metadata, participant.name, participant.identity]);

  const muteParticipantTrack = useVideoStore(s => s.muteParticipantTrack);
  const [muteBusy, setMuteBusy] = useState(false);
  const canModerate = showTeacherControls && !isLocal && !isTeacher;

  const handleMute = useCallback(async (source) => {
    if (muteBusy) return;
    setMuteBusy(true);
    try {
      const result = await muteParticipantTrack(participant.identity, source);
      if (!result.success) {
        console.warn('[LiveKitVideoGallery] mute failed:', result.error);
      }
    } finally {
      setMuteBusy(false);
    }
  }, [muteBusy, muteParticipantTrack, participant.identity]);

  return (
    <div className="group flex-shrink-0 h-16 md:h-20 aspect-video bg-brand-cream rounded-lg overflow-hidden border-2 border-brand-primary relative">
      {hasVideo ? (
        <video
          ref={setVideoEl}
          autoPlay
          playsInline
          muted={isLocal}
          className="w-full h-full object-cover"
          style={{ transform: isLocal ? 'scaleX(-1)' : 'none' }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 bg-brand-light rounded-full mx-auto mb-1 flex items-center justify-center">
              <Users size={16} className="text-brand-primary" />
            </div>
            <p className="text-[10px] text-brand-primary font-medium truncate max-w-[80px]">
              {isTeacher ? t('video.teacher') : displayName}
            </p>
          </div>
        </div>
      )}

      {/* Mute indicator */}
      {isMicMuted && (
        <div className="absolute bottom-1 left-1 bg-red-500 rounded-full p-0.5">
          <MicOff size={8} className="text-white" />
        </div>
      )}

      {/* Name overlay when video is on */}
      {hasVideo && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-1 py-0.5">
          <p className="text-[9px] text-white font-medium truncate">
            {isTeacher ? t('video.teacher') : displayName}
          </p>
        </div>
      )}

      {/* Local indicator */}
      {isLocal && (
        <div className="absolute top-1 right-1 bg-brand-primary text-white text-[7px] px-1 py-0.5 rounded">
          {t('video.you')}
        </div>
      )}

      {/* Teacher moderation controls — hover-visible on remote student tiles */}
      {canModerate && (
        <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/40 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button
            onClick={() => handleMute('microphone')}
            disabled={muteBusy || isMicMuted}
            className="p-1.5 rounded-full bg-white/90 hover:bg-white text-brand-primary disabled:opacity-40 disabled:cursor-not-allowed transition"
            title={isMicMuted ? t('video.micAlreadyOff') : t('video.muteParticipantMic')}
            aria-label={t('video.muteParticipantMic')}
          >
            {isMicMuted ? <MicOff size={14} /> : <Mic size={14} />}
          </button>
          <button
            onClick={() => handleMute('camera')}
            disabled={muteBusy || isCameraMuted}
            className="p-1.5 rounded-full bg-white/90 hover:bg-white text-brand-primary disabled:opacity-40 disabled:cursor-not-allowed transition"
            title={isCameraMuted ? t('video.cameraAlreadyOff') : t('video.muteParticipantCamera')}
            aria-label={t('video.muteParticipantCamera')}
          >
            {isCameraMuted ? <VideoOff size={14} /> : <Camera size={14} />}
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * LiveKit video gallery - shows real video streams
 * MUST be used inside LiveKitRoom context
 *
 * On mobile (< md) only the tile at `mobileActiveIndex` is visible so the user
 * can cycle participants one-at-a-time via parent-provided arrow buttons. On
 * desktop (>= md), all tiles render as before.
 */
const LiveKitVideoGallery = ({ mobileActiveIndex = 0, onCountChange }) => {
  const remoteParticipants = useParticipants();
  const { localParticipant } = useLocalParticipant();

  // Local user is a room host (teacher of this lesson / admin) when the token
  // stamped role=teacher into their participant metadata. resolveRoom on the
  // backend is the source of truth — the metadata just mirrors it so the UI
  // knows whether to render moderation controls.
  const isLocalTeacher = useMemo(
    () => parseRole(localParticipant?.metadata) === 'teacher',
    [localParticipant?.metadata]
  );

  // Build full participants list: local first, then remote
  const allParticipants = useMemo(() => {
    const list = [];
    if (localParticipant) {
      list.push(localParticipant);
    }
    // Filter out local participant from remote list (sometimes included)
    const remotes = remoteParticipants.filter(p => !p.isLocal);
    list.push(...remotes);
    return list;
  }, [localParticipant, remoteParticipants]);

  useEffect(() => {
    onCountChange?.(allParticipants.length);
  }, [allParticipants.length, onCountChange]);

  return (
    <>
      {allParticipants.map((participant, index) => (
        <div
          key={participant.identity}
          className={index === mobileActiveIndex ? 'contents' : 'hidden md:contents'}
        >
          <ParticipantTile
            participant={participant}
            isLocal={participant.isLocal}
            showTeacherControls={isLocalTeacher}
          />
        </div>
      ))}
    </>
  );
};

export default LiveKitVideoGallery;
